/* eslint-disable no-plusplus */
import { useMediaQuery } from '@chakra-ui/react';
import { SdgCategory } from '#core/SdgQuiz/types';
import contentJson from '#core/SdgQuiz/utils/content.json';

export const extractSdgs = (content: Record<string, any>): string[] =>
  Object.keys(content)
    .filter((keyName: string) => keyName.startsWith('sdg'))
    .map((keyName: string) => keyName.replace('sdg', ''));

export const countSdgs = (content: any): number =>
  Object.keys(content).filter((key: string) => key.startsWith('sdg')).length;

export const countTotalQuestions = (currentContent: any): number =>
  (Object.values(currentContent) as any[]).reduce(
    (acc: number, curr: any) => acc + curr.length,
    0,
  );

export const countFilledSdgs = (data: any, sdgNumber: number): number => {
  const sdgPrefix = `sdg${sdgNumber}`;

  return Object.keys(data).filter(
    (key: string) => key.startsWith(sdgPrefix) && data[key] !== '',
  ).length;
};

export const countAnswersPoints = (
  data: Record<string, string>,
  sdgNumber: number,
): number => {
  const sdgPrefix = `sdg${sdgNumber}`;

  return Object.entries(data).reduce((totalScore, [key, value]) => {
    if (key.startsWith(sdgPrefix)) {
      if (value === 'Yes') {
        return totalScore + 1;
      }

      if (value.includes('Maybe')) {
        return totalScore + 0.5;
      }
    }
    return totalScore;
  }, 0);
};

export const calculatePercentage = (numerator: number, denominator: number) => {
  if (denominator === 0) {
    return 0;
  }

  return (numerator / denominator) * 100;
};

export const countParticipatedSDGs = (data: any) => {
  const uniqueSDG = new Set();
  Object.keys(data).forEach((key: any) => {
    if (key.startsWith('sdg')) {
      const sdgNumber = key.match(/\d+/)[0];
      uniqueSDG.add(sdgNumber);
    }
  });

  return uniqueSDG.size;
};

export const calculateSDGRelevance = (
  content: Record<string, any>,
  data: Record<string, string>,
  numberOfSdgs: number,
) => {
  const relevance: {
    relevant: number[];
    partialRelevant: number[];
    notRelevant: number[];
    skipped: number[];
  } = {
    relevant: [],
    partialRelevant: [],
    notRelevant: [],
    skipped: [],
  };

  for (let i = 0; i < numberOfSdgs; i++) {
    const sdgNumber = i + 1;
    const currentContent = content[`sdg${sdgNumber}`]?.questions || [];
    const totalQuestionsCount = countTotalQuestions(currentContent);
    const numberOfFilledSdgs = countFilledSdgs(data, sdgNumber);

    const areAllFieldsFilledIn = numberOfFilledSdgs === totalQuestionsCount;

    const answersPoints = countAnswersPoints(data, sdgNumber);

    const percentage = areAllFieldsFilledIn
      ? calculatePercentage(answersPoints, totalQuestionsCount)
      : -1;

    const { relevancePercentages } = content;

    if (percentage > relevancePercentages.medium) {
      relevance.relevant.push(sdgNumber);
    } else if (
      percentage > relevancePercentages.low &&
      percentage <= relevancePercentages.medium
    ) {
      relevance.partialRelevant.push(sdgNumber);
    } else if (percentage >= 0 && percentage <= relevancePercentages.low) {
      relevance.notRelevant.push(sdgNumber);
    } else {
      relevance.skipped.push(sdgNumber);
    }
  }

  return relevance;
};

export const formSdgCategories = (data: any): SdgCategory[] => {
  const filteredData: any = {};

  Object.keys(data).forEach((key: string) => {
    if (key.startsWith('sdg')) {
      const { category } = data[key];
      filteredData[category] = filteredData[category] || {
        categoryTitle: category,
        numberOfSdgsPerCategory: 0,
        imgSpritePositionsMobile: [],
        imgSpritePositionsDesktop: [],
      };
      filteredData[category].numberOfSdgsPerCategory++;
      filteredData[category].imgSpritePositionsMobile.push(
        data[key].imgSpritePositionMobileLarge,
      );
      filteredData[category].imgSpritePositionsDesktop.push(
        data[key].imgSpritePositionDesktop,
      );
    }
  });

  return Object.values(filteredData);
};

export const useResponsive = () => {
  const [isMobileScreen] = useMediaQuery(
    `(max-width: ${contentJson.imgContainerSizes.containerWidthMobile})`,
  );
  return isMobileScreen;
};
